import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Auth.css';
import { apiCall } from '../utils/api';
import { toast } from 'react-toastify';

const InstitutionRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    institutionName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'India',
    foundingYear: '',
    charges: ''
  });

  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.institutionName.trim()) {
      newErrors.institutionName = 'Institution name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10,}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number must be at least 10 digits';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    }

    if (step === 2 && !certificateFile) {
      newErrors.certificate = 'Certificate document is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificateFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCertificatePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCertificate = () => {
    setCertificateFile(null);
    setCertificatePreview(null);
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (step === 1) {
      // Validate only step 1 fields
      const step1Errors = {};
      if (!formData.institutionName.trim()) step1Errors.institutionName = 'Institution name is required';
      if (!formData.email) {
        step1Errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        step1Errors.email = 'Email is invalid';
      }
      if (!formData.password) {
        step1Errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        step1Errors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        step1Errors.confirmPassword = 'Passwords do not match';
      }

      if (Object.keys(step1Errors).length === 0) {
        setStep(2);
        window.scrollTo(0, 0);
      } else {
        setErrors(step1Errors);
      }
    }
  };

  const handlePreviousStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const formDataToSend = new FormData();

        // Append all text fields
        Object.keys(formData).forEach(key => {
          formDataToSend.append(key, formData[key]);
        });

        formDataToSend.append('role', 'institution');

        // Append file
        if (certificateFile) {
          formDataToSend.append('certificate', certificateFile);
        }

        await apiCall('/auth/register', 'POST', formDataToSend, true);

        toast.success('Institution registered successfully! You can now login.');
        navigate('/login');

      } catch (error) {
        toast.error(error.message || 'Registration failed');
      }
    }
  };

  const renderStepProgress = () => (
    <div className="step-progress">
      <div className={`step ${step >= 1 ? 'active' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Account Details</div>
      </div>
      <div className={`step-connector ${step >= 2 ? 'active' : ''}`}></div>
      <div className={`step ${step >= 2 ? 'active' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Institution Details</div>
      </div>
    </div>
  );

  const renderStepOne = () => (
    <div >
      <h2>Create Your Account</h2>

      <div className="form-row" >
        <div className="form-group">
          <label htmlFor="institutionName">Institution Name <span className="required">*</span></label>
          <input
            type="text"
            id="institutionName"
            name="institutionName"
            placeholder="Enter institution name"
            value={formData.institutionName}
            onChange={handleChange}
            className={errors.institutionName ? 'error' : ''}
          />
          {errors.institutionName && <span className="error-text">{errors.institutionName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address <span className="required">*</span></label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Enter institution email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="password">Password <span className="required">*</span></label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              placeholder="Create a strong password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="toggle-password"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {errors.password && <span className="error-text">{errors.password}</span>}
          <div className="password-hint">Must be at least 6 characters long</div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password <span className="required">*</span></label>
          <div className="password-input">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="toggle-password"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={handleNextStep} className="btn btn-primary">
          Next <span className="icon">→</span>
        </button>
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div>
      <h2>Institution Information</h2>

      <div className="form-row" >
        <div className="form-group">
          <label htmlFor="phone">Phone Number <span className="required">*</span></label>
          <input
            type="tel"
            id="phone"
            name="phone"
            placeholder="Enter phone number"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? 'error' : ''}
          />
          {errors.phone && <span className="error-text">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="foundingYear">Founding Year</label>
          <input
            type="number"
            id="foundingYear"
            name="foundingYear"
            placeholder="e.g., 2010"
            min="1900"
            max={new Date().getFullYear()}
            value={formData.foundingYear}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="address">Address <span className="required">*</span></label>
          <input
            type="text"
            id="address"
            name="address"
            placeholder="Enter street address"
            value={formData.address}
            onChange={handleChange}
            className={errors.address ? 'error' : ''}
          />
          {errors.address && <span className="error-text">{errors.address}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="city">City <span className="required">*</span></label>
          <input
            type="text"
            id="city"
            name="city"
            placeholder="Enter city"
            value={formData.city}
            onChange={handleChange}
            className={errors.city ? 'error' : ''}
          />
          {errors.city && <span className="error-text">{errors.city}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="state">State <span className="required">*</span></label>
          <input
            type="text"
            id="state"
            name="state"
            placeholder="Enter state"
            value={formData.state}
            onChange={handleChange}
            className={errors.state ? 'error' : ''}
          />
          {errors.state && <span className="error-text">{errors.state}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="postalCode">Postal Code <span className="required">*</span></label>
          <input
            type="text"
            id="postalCode"
            name="postalCode"
            placeholder="Enter postal code"
            value={formData.postalCode}
            onChange={handleChange}
            className={errors.postalCode ? 'error' : ''}
          />
          {errors.postalCode && <span className="error-text">{errors.postalCode}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="country">Country</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            disabled
          />
        </div>
        <div className="form-group">
          <label htmlFor="charges">Charges</label>
          <input
            type="text"
            id="charges"
            name="charges"
            placeholder="Enter charges"
            value={formData.charges}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="certificate">📄 Upload Certificates/Documents <span className="required">*</span></label>
        <div className="certificate-upload-section">
          <div className={`file-input-wrapper ${errors.certificate ? 'error' : ''}`}>
            <input
              type="file"
              id="certificate"
              name="certificate"
              onChange={handleCertificateChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            <label htmlFor="certificate" className="file-input-label">
              📁 Choose File (PDF, DOC, IMG)
            </label>
          </div>
          {errors.certificate && <span className="error-text">{errors.certificate}</span>}

          {certificatePreview && (
            <div className="certificate-preview">
              <p className="preview-title">📋 Certificate Preview:</p>
              {certificateFile.type.startsWith('image/') ? (
                <img src={certificatePreview} alt="Certificate" className="preview-image" />
              ) : (
                <div className="preview-document">
                  <p>📄 {certificateFile.name}</p>
                  <p className="file-size">({(certificateFile.size / 1024).toFixed(2)} KB)</p>
                </div>
              )}
              <button
                type="button"
                onClick={removeCertificate}
                className="remove-certificate-btn"
              >
                ✕ Remove
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={handlePreviousStep} className="btn btn-secondary">
          ← Back
        </button>
        <button type="submit" className="btn btn-primary">
          Complete Registration
        </button>
      </div>
    </div>
  );

  return (
    <div className="auth-container">
      <div className="auth-form-wrapper institution-register">
        <form onSubmit={step === 1 ? handleNextStep : handleSubmit}>
          <div>
            {renderStepProgress()}
            {step === 1 ? renderStepOne() : renderStepTwo()}
          </div>

          <p className="login-link">
            Already have an account? <a href="/login">Login here</a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default InstitutionRegister;
